import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import secureAxios from "../../utils/secureAxios";
import EditPlanForm from "./EditPlanForm";
import ViewPlanDetails from "./ViewPlanDetails";
import { useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import ConfidentialityNotice from "../common/ConfidentialityNotice";
import SecurityInformation from "../common/SecurityInformation";

const PlanList = forwardRef((props, ref) => {
  const [plans, setPlans] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedConsumer, setSelectedConsumer] = useState("");
  const [editingPlan, setEditingPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const { tenant_id, user } = useContext(AuthContext);

  const apiBase = getApiBaseURL();

  useEffect(() => {
    if (tenant_id && user?.user_id && user?.role) {
      fetchConsumers();
      fetchPlans();
    }
  }, [tenant_id, user]);

  const fetchConsumers = async () => {
    try {
      const res = await secureAxios.get(`${apiBase}/api/consumers`, {
        params: {
          tenant_id,
          user_id: user.user_id,
          role: user.role,
          tenant_type: user.tenant_type,
        },
      });

      setConsumers(res.data);
    } catch (err) {
      // Log error silently in production
      setErrorMessage("Failed to load consumers. Please refresh the page.");
    }
  };

  const fetchPlans = () => {
    if (!tenant_id || !user?.user_id || !user?.role) return;

    secureAxios
      .get(`${apiBase}/api/plans`, {
        params: { tenant_id, user_id: user.user_id, role: user.role },
      })
      .then((res) => {
        setPlans(res.data);
        setLoading(false);
      })
      .catch((err) => {
        // Log error silently in production
        setErrorMessage("Failed to load plans. Please refresh the page.");
        setLoading(false);
      });
  };

  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchPlans();
    },
  }));

  const handleSearchChange = (e) => {
    setSearch(e.target.value.toLowerCase());
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
  };

  const handleFinishEdit = () => {
    setEditingPlan(null);
    fetchPlans();
  };

  const filteredPlans = search.trim()
    ? plans.filter((p) =>
        p.consumer_name?.toLowerCase().includes(search.trim().toLowerCase())
      )
    : plans;

  if (!user) return <p>Loading user...</p>;

  return (
    <div>
      {!editingPlan && !viewingPlan && (
        <>
          <h2>All Plans</h2>

          {/* HIPAA Notice for List View */}

          <ConfidentialityNotice />

          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Search by Consumer:</label>
            <input
              type="text"
              className="form-control"
              placeholder="Type a consumer name..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </>
      )}

      {/* ✏️ Edit Mode or 📋 Table */}
      {editingPlan ? (
        <EditPlanForm
          plan={editingPlan}
          onCancel={handleFinishEdit}
          onUpdated={handleFinishEdit}
        />
      ) : viewingPlan ? (
        <ViewPlanDetails
          plan={viewingPlan}
          onClose={() => setViewingPlan(null)}
        />
      ) : (
        <>
          {/* ✅ Desktop/Tablet Table View */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th>Plan Name</th>
                  <th>Consumer</th>
                  <th>Initial</th>
                  <th>Updated</th>
                  <th>Counselor</th>
                  <th>Services</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredPlans.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No matching plans found.
                    </td>
                  </tr>
                ) : (
                  filteredPlans.map((p) => (
                    <tr key={p.plan_id}>
                      <td>{p.plan_name || "—"}</td>
                      <td>{p.consumer_name}</td>
                      <td>{p.initial_plan ? "✔️" : ""}</td>
                      <td>{p.updated_plan ? "✔️" : ""}</td>
                      <td>{p.counselor_name || "—"}</td>
                      <td>
                        {p.services?.length > 0
                          ? p.services.map((s) => s.service_name).join(", ")
                          : "—"}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setViewingPlan(p)}
                          >
                            View
                          </button>
                          {(p.created_by === user.user_id ||
                            user.role?.toLowerCase() === "admin") && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEdit(p)}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="7" className="text-muted text-center small">
                    <i className="bi bi-info-circle me-1"></i>
                    Showing {filteredPlans.length} plan(s). All access is logged
                    for HIPAA compliance.
                    {user?.role?.toLowerCase() === "admin" && (
                      <span className="ms-2">
                        Admin users can edit and delete any plan.
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ✅ Mobile-Friendly List View */}
          <ul className="list-group d-block d-md-none">
            {loading ? (
              <li className="list-group-item text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </li>
            ) : filteredPlans.length === 0 ? (
              <li className="list-group-item text-center">
                No matching plans found.
              </li>
            ) : (
              filteredPlans.map((p) => (
                <li key={p.plan_id} className="list-group-item">
                  <div>
                    <strong>Plan Name:</strong> {p.plan_name || "—"}
                  </div>
                  <div>
                    <strong>Consumer:</strong> {p.consumer_name}
                  </div>
                  <div>
                    <strong>Initial:</strong> {p.initial_plan ? "✔️" : "—"}
                  </div>
                  <div>
                    <strong>Updated:</strong> {p.updated_plan ? "✔️" : "—"}
                  </div>
                  <div>
                    <strong>Counselor:</strong> {p.counselor_name || "—"}
                  </div>
                  <div>
                    <strong>Services:</strong>{" "}
                    {p.services?.length > 0
                      ? p.services.map((s) => s.service_name).join(", ")
                      : "—"}
                  </div>
                  <div className="mt-2 d-flex gap-2">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => setViewingPlan(p)}
                    >
                      View
                    </button>
                    {(p.created_by === user.user_id ||
                      user.role?.toLowerCase() === "admin") && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(p)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>

          {/* Footer message for mobile view */}
          {!loading && (
            <div className="text-muted text-center small mt-3 d-block d-md-none">
              <i className="bi bi-info-circle me-1"></i>
              Showing {filteredPlans.length} plan(s). All access is logged for
              HIPAA compliance.
              {user?.role?.toLowerCase() === "admin" && (
                <span className="d-block mt-1">
                  Admin users can edit and delete any plan.
                </span>
              )}
            </div>
          )}
        </>
      )}
      <SecurityInformation />
    </div>
  );
});

export default PlanList;
